import { useState, useEffect } from 'react';
import { CircleNotch, ArrowsClockwise } from '@phosphor-icons/react';
import { API } from './constants';

export const InboxAutoSyncSettings = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const refresh = () => {
    fetch(`${API}/email/inbox/auto-sync/settings`, { credentials: 'include' })
      .then(r => r.json()).then(setSettings).catch(() => {});
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const res = await fetch(`${API}/email/inbox/auto-sync/settings`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: settings.enabled, interval_minutes: settings.interval_minutes }),
    });
    const data = await res.json();
    setSettings(s => ({ ...s, ...data }));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleSyncNow = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/graph/inbox/sync`, { method: 'POST', credentials: 'include' });
      setLastResult(await res.json());
      setTimeout(refresh, 500);
    } catch { /* ignore */ }
    setRunning(false);
  };

  if (!settings) return null;

  const formatTime = (iso) => iso ? new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : 'nog nooit';

  return (
    <div className="mt-8 space-y-4 border-t border-zinc-200 pt-8" data-testid="inbox-autosync-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-base font-semibold">Mailbox Auto-Sync</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Automatisch nieuwe e-mails ophalen uit MS Graph mailboxen — draait op de achtergrond</p>
        </div>
        <div className="flex items-center gap-2">
          {saved && <span className="text-xs text-emerald-600 font-medium">Opgeslagen!</span>}
          <button onClick={handleSyncNow} disabled={running} data-testid="autosync-run-now-btn"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-zinc-200 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 transition-colors">
            {running ? <CircleNotch size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />} Nu syncen
          </button>
          <button onClick={handleSave} disabled={saving} data-testid="save-autosync-btn"
            className="px-4 py-2 text-sm font-medium bg-[#0055FF] text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white p-5 space-y-5">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!settings.enabled}
            onChange={e => setSettings(s => ({ ...s, enabled: e.target.checked }))}
            className="w-4 h-4 accent-[#0055FF]" data-testid="autosync-enabled-toggle" />
          <span className="text-sm font-medium">Auto-sync ingeschakeld</span>
          <span className="text-xs text-zinc-400">{settings.enabled ? 'Draait op de achtergrond' : 'Uitgeschakeld'}</span>
        </label>

        <div>
          <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 block">Sync interval (minuten)</label>
          <div className="flex items-center gap-3">
            <input type="number" min="1" max="120" value={settings.interval_minutes || 2}
              onChange={e => setSettings(s => ({ ...s, interval_minutes: Math.max(1, parseInt(e.target.value) || 1) }))}
              className="border border-zinc-200 px-3 py-1.5 text-sm rounded-sm w-24" data-testid="autosync-interval-input" />
            <span className="text-xs text-zinc-500">Hoe vaak de server e-mails ophaalt uit info@ + m.wirken@ poolfix.nl</span>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {[1, 2, 5, 10, 15, 30].map(m => (
              <button key={m} type="button" onClick={() => setSettings(s => ({ ...s, interval_minutes: m }))}
                data-testid={`autosync-preset-${m}`}
                className={`px-2.5 py-1 text-xs border transition-colors ${settings.interval_minutes === m ? 'bg-[#0055FF] text-white border-[#0055FF]' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'}`}>
                {m} min
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-zinc-100">
          <div>
            <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">MS Graph</div>
            <div className={`text-sm font-medium ${settings.graph_configured ? 'text-emerald-600' : 'text-red-500'}`}>
              {settings.graph_configured ? 'Verbonden' : 'Niet geconfigureerd'}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Loop status</div>
            <div className={`text-sm font-medium ${settings.running ? 'text-emerald-600' : 'text-zinc-400'}`}>
              {settings.running ? 'Actief' : 'Gestopt'}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Laatste run</div>
            <div className="text-sm">{formatTime(settings.last_run)}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Laatste resultaat</div>
            <div className="text-sm">
              {settings.last_result?.synced != null ? `${settings.last_result.synced} nieuw` : '-'}
            </div>
          </div>
        </div>

        {settings.last_error && (
          <div className="text-xs px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-sm" data-testid="autosync-error">
            <strong>Fout:</strong> {settings.last_error}
          </div>
        )}

        {lastResult && (
          <div className="text-xs px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm">
            Handmatige sync: <strong>{lastResult.synced ?? 0}</strong> nieuwe email(s) verwerkt.
          </div>
        )}
      </div>
    </div>
  );
};
